import { CURRENT_CHAIN } from '@hyperswap/addresses'
import { Currency, WETH9 } from '@uniswap/sdk-core'
import { FeeAmount } from '@uniswap/v3-sdk'
import axios from 'axios'
import Loader from 'components/Loader'
import TagButton from 'components/TagButton'
import { API_POOL_BASE_URL } from 'constants/links'
import { useActiveWeb3React } from 'hooks'
import { useV3Factory } from 'hooks/useContract'
import { ChartHeader, ChartHeaderTitle, ChartTitle, TooltipContainer } from 'pages/ExplorePools/Metrics/styled'
import React from 'react'
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import styled from 'styled-components'

const ContainerStyle = styled.div`
  cursor: default;
  width: 100%;
  height: 226px;
  max-height: 250px;
  padding: 16px;
  border-radius: 16px;
  background: ${({ theme }) => theme.bg1};
  overflow: hidden;
`

interface ChartLiquidityProps {
  currencyIdA: string | undefined
  currencyIdB: string | undefined
  tokenASymbol: string | null
  tokenBSymbol: string | null
  feeAmount: FeeAmount | undefined
  currencyA: Currency
  currencyB: Currency
  invertedFromAPI: boolean
}

const ChartLiquidity: React.FC<ChartLiquidityProps> = ({
  currencyIdA,
  currencyIdB,
  feeAmount,
  tokenASymbol,
  tokenBSymbol,

  invertedFromAPI,
}) => {
  const { library } = useActiveWeb3React()
  const factoryContract = useV3Factory()
  const [loading, setLoading] = React.useState(false)
  const [data, setData] = React.useState([])
  const [pairAddress, setPairAddress] = React.useState<null | string>(null)
  // Track previous input values
  const previousInputs = React.useRef<{
    currencyIdA?: string
    currencyIdB?: string
    feeAmount?: FeeAmount
  }>({})

  React.useEffect(() => {
    const fetchData = async () => {
      if (!factoryContract || !currencyIdA || !currencyIdB || !feeAmount || !library) {
        return
      }

      const tokenAAddress = currencyIdA === 'HYPE' ? WETH9[CURRENT_CHAIN].address : currencyIdA
      const tokenBAddress = currencyIdB === 'HYPE' ? WETH9[CURRENT_CHAIN].address : currencyIdB

      try {
        setLoading(true)

        const pairAddress = await factoryContract.getPool(tokenAAddress, tokenBAddress, feeAmount)
        /*    const poolContract = new Contract(
          pairAddress,
          ['function token0() view returns (address)', 'function token1() view returns (address)'],
          library
        )

        const token1 = await poolContract.token1() */

        setPairAddress(pairAddress)
        const response = await axios.get(`${API_POOL_BASE_URL}/api/pair-depth`, {
          params: {
            pairAddress,
          },
        })

        setData(response?.data?.data ? response?.data?.data : [])
        setLoading(false)
      } catch (e) {
        console.warn(e)
        setData([])
        setLoading(false)
      } finally {
        setLoading(false)
      }
    }

    const sameAsPrevious =
      previousInputs.current.currencyIdA === currencyIdA &&
      previousInputs.current.currencyIdB === currencyIdB &&
      previousInputs.current.feeAmount === feeAmount

    if (sameAsPrevious) return
    previousInputs.current = { currencyIdA, currencyIdB, feeAmount }

    if (!sameAsPrevious) {
      fetchData()
    }
  }, [factoryContract, currencyIdA, currencyIdB, feeAmount, library, tokenASymbol, tokenBSymbol])

  const shouldInvert = invertedFromAPI

  const transformedData = React.useMemo(() => {
    if (!shouldInvert) {
      return [...data].sort((a: any, b: any) => Number(a.price) - Number(b.price))
    }

    return [...data]
      .map((item: any) => ({
        ...item,
        price: 1 / Number(item.price),
        side: item.side === 'bid' ? 'ask' : 'bid',
      }))
      .sort((a, b) => Number(a.price) - Number(b.price))
  }, [data, shouldInvert])

  return (
    <>
      {loading === true ? (
        <>
          <ContainerStyle style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Loader size="32px" />
          </ContainerStyle>
        </>
      ) : (
        <>
          {transformedData.length > 0 ? (
            <ContainerStyle>
              <ChartHeader>
                <ChartHeaderTitle>
                  <ChartTitle>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      Depth chart{' '}
                      <TagButton onClick={() => window.open(`https://purrsec.com/address/${pairAddress}`, '_blank')}>
                        <svg
                          viewBox="0 0 16 16"
                          xmlns="http://www.w3.org/2000/svg"
                          style={{ width: '18px', background: 'black', borderRadius: '100px' }}
                        >
                          <line x1="1" y1="5" x2="15" y2="12" strokeWidth="1" stroke="currentColor"></line>
                          <line x1="1" y1="11" x2="15" y2="9" strokeWidth="1" stroke="currentColor"></line>
                        </svg>
                        View on Purrsec
                      </TagButton>
                    </div>
                  </ChartTitle>
                </ChartHeaderTitle>
              </ChartHeader>
              <ResponsiveContainer width="100%" height={185}>
                <BarChart data={transformedData} barSize={20}>
                  <XAxis
                    dataKey="price"
                    tickFormatter={(value) => Number(value).toFixed(5)}
                    tick={{ fontSize: 10, fill: '#C3C5CB' }}
                  />

                  <YAxis dataKey="qty" tick={{ fontSize: 10, fill: '#C3C5CB' }} orientation="right" />
                  <defs>
                    <linearGradient id="colorbid" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#4AFB39" stopOpacity={0.9} />
                      <stop offset="100%" stopColor="#4AFB39" stopOpacity={0.3} />
                    </linearGradient>
                    <linearGradient id="colorAsk" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#FD4040" stopOpacity={0.9} />
                      <stop offset="100%" stopColor="#FD4040" stopOpacity={0.3} />
                    </linearGradient>
                  </defs>
                  <Bar dataKey="qty">
                    {transformedData.map((entry: any, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.side === 'ask' ? 'url(#colorAsk)' : 'url(#colorbid)'}
                        fillOpacity={0.9}
                      />
                    ))}
                  </Bar>

                  <Tooltip
                    cursor={{ fill: '#1F1C29A1' }}
                    content={({ payload }) => {
                      if (payload && payload.length > 0) {
                        const data = payload[0].payload

                        return (
                          <TooltipContainer>
                            <div className="tooltip__volume">
                              <span className="tooltip__volume-title">Price</span>
                              <span className="tooltip__volume-value">{data.price}</span>
                              <span className="tooltip__volume-title">Liquidity</span>
                              <span className="tooltip__volume-value">{data.qty}</span>
                            </div>
                          </TooltipContainer>
                        )
                      }
                      return null
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ContainerStyle>
          ) : (
            <></>
          )}
        </>
      )}
    </>
  )
}

export default ChartLiquidity
